<template>
  <component
    :is="tag"
    :id="id"
    :href="href"
    :to="to"
    :type="type"
    class="c-button"
    :disabled="isDisabled"
    :class="{
      'c-button--active': active,
      'c-button--disabled': isDisabled,
      [`c-button--${size}`]: size,
      [`c-button--${variant}`]: variant,
      'c-button--squared': shape == 'squared',
    }"
    @click="handleClick"
  >
    <div
      v-if="loading"
      class="c-button__loader"
    >
      <CSpinner
        color="white"
        size="small"
      />
    </div>
    <slot v-else />
  </component>
</template>

<script setup lang="ts">
import { computed, resolveComponent } from 'vue'
import CSpinner from '../CSpinner'

interface Props {
  active?: boolean
  isDisabled?: boolean
  variant?:
    | 'primary'
    | 'secondary'
    | 'green'
    | 'plain'
    | 'plain-light'
    | 'outlined'
    | 'outlined--clicked'
    | 'secondary-no-border'
    | 'secondary--clicked'
  shape?: 'squared' | 'rounded'
  size?: 'small' | 'medium' | 'large'
  type?: 'button' | 'submit' | 'reset'
  href?: string
  id?: string
  to?: string | object // Object should probably be RouteLocationRaw but we can't import types yet
  loading?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  active: false,
  isDisabled: false,
  variant: 'primary',
  size: 'medium',
  type: 'button',
  shape: 'rounded',
  href: undefined,
  id: undefined,
  to: undefined,
  loading: false,
})

const emit = defineEmits<{
  (e: 'click', event: Event): void
}>()

const handleClick = (event: Event) => {
  if (!props.loading) emit('click', event)
}

const tag = computed(() => {
  if (props.href || props.to) return resolveComponent('nuxt-link')
  return 'button'
})
</script>

<script lang="ts">
import { defineComponent } from 'vue'

export default defineComponent({
  name: 'CButton',
})
</script>
